import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { EToastItemType } from 'projects/shared/lib/enum/toast/toast-entry-type.enum';
import { ToastItem } from 'projects/shared/lib/components/shared/toast/item/toast-item.model';
import { EError, UserPasswordSetCommand } from 'projects/shared/lib/services/INTEGRA_Shop_Client';
import { LoggerService } from 'projects/shared/lib/services/infrastructure/logger/logger.service';
import { ENVIRONMENT } from 'projects/shared/lib/environment/environment';
import { IUserService, IUserServiceToken } from 'projects/shared/lib/services/data/user/user-service.interface';
import { ErrorCodeService } from 'projects/shared/lib/services/infrastructure/error-code.service';
import { ServiceLocator } from 'projects/shared/lib/services/infrastructure/service-locator.service';
import { IToastService, IToastServiceToken } from 'shared/services/ui/toast/toast-service.interface';

@Component({
  selector: 'app-user-password-set-dialog',
  imports: [
    CommonModule,
    FormsModule,
  ],
  templateUrl: './user-password-set-dialog.component.html',
  styleUrls: ['./user-password-set-dialog.component.scss']
})
export class UserPasswordSetDialogComponent {
  @Input() public modalTitle?: string;
  @Input() public submitButtonLabel: string;
  @Input() public cancelButtonLabel: string;
  @Output() public modalSubmit: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  @Output() public modalCancel: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();
  public passwordOld: string;
  public passwordNew1: string;
  public passwordNew2: string;
  public passwordOldIsValid: boolean;
  public passwordNewIsValid: boolean;
  public passwordNewMatching: boolean;
  public formIsValid: boolean;
  public passwordOldShow: boolean;
  public passwordNew1Show: boolean;
  public passwordNew2Show: boolean;

  public passwordMinLength: number;
  public passwordMaxLength: number;

  private logger: LoggerService;
  private readonly toastService: IToastService = inject(IToastServiceToken);
  private readonly userService: IUserService = inject(IUserServiceToken);

  public constructor() {
    this.logger = ServiceLocator.injector.get(LoggerService);

    this.submitButtonLabel = $localize`:@@UserPasswordSetDialogSubmitButtonDefaultLabel:Accept`;
    this.cancelButtonLabel = $localize`:@@UserPasswordSetDialogCancelButtonDefaultLabel:Cancel`;
    this.passwordOld = '';
    this.passwordNew1 = '';
    this.passwordNew2 = '';
    this.passwordOldIsValid = false;
    this.passwordNewIsValid = false;
    this.passwordNewMatching = true;
    this.formIsValid = false;
    this.passwordOldShow = false;
    this.passwordNew1Show = false;
    this.passwordNew2Show = false;

    this.passwordMinLength = ENVIRONMENT.auth.passwordMinLength;
    this.passwordMaxLength = ENVIRONMENT.auth.passwordMaxLength;
  }

  public passwordOldShowClick(): void {
    this.passwordOldShow = !(this.passwordOldShow);
  }

  public passwordNew1ShowClick(): void {
    this.passwordNew1Show = !(this.passwordNew1Show);
  }

  public passwordNew2ShowClick(): void {
    this.passwordNew2Show = !(this.passwordNew2Show);
  }

  public async submitAsync(event: MouseEvent) {
    // form validation
    let form = document.getElementById('userPasswordSetDialogForm') as HTMLFormElement;

    if (form.checkValidity() === false || this.formValidate() === false) {
      form.classList.add('was-validated');
      return;
    }

    // valid
    let success: boolean = await this.passwordSetClickAsync();
    if (success) {
      this.modalSubmit.emit(event);
    }
  }

  cancel(event: MouseEvent) {
    this.modalCancel.emit(event);
  }

  public async passwordSetClickAsync(): Promise<boolean> {
    let passwordOldInvalid: boolean = !this.passwordOld || this.passwordOld.length <= 0;
    if (passwordOldInvalid) {
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        title: $localize`:@@DialogUserPasswordSetPasswordOldInvalidTitle:Old password missing`,
        message: $localize`:@@DialogUserPasswordSetPasswordOldInvalidMesssage:You old password password is missing. Please insert.`,
        code: ErrorCodeService.formatErrorCode(EError.F200030)
      }));
      return false;
    }

    let passwordNew1Invalid: boolean = !this.passwordNew1 || this.passwordNew1.length < this.passwordMinLength || this.passwordNew1.length > this.passwordMaxLength;
    let passwordNew2Invalid: boolean = !this.passwordNew2 || this.passwordNew2.length < this.passwordMinLength || this.passwordNew2.length > this.passwordMaxLength;
    if (passwordNew1Invalid || passwordNew2Invalid || this.passwordNewMatching == false) {
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        title: $localize`:@@DialogUserPasswordSetPasswordNewInvalidTitle:New password invalid`,
        message: $localize`:@@DialogUserPasswordSetPasswordNewInvalidMesssage:You new password is invalid. Please verify again.`,
        code: ErrorCodeService.formatErrorCode(EError.F200040)
      }));
      return false;
    }

    let userPasswordSetCommand: UserPasswordSetCommand = new UserPasswordSetCommand();
    userPasswordSetCommand.passwordOld = this.passwordOld;
    userPasswordSetCommand.passwordNew = this.passwordNew2;

    return await firstValueFrom(this.userService.userPasswordSet(userPasswordSetCommand)).then((userPasswordSetCommandResult) => {
      if (userPasswordSetCommandResult) {
        this.toastService.toastItemAdd(new ToastItem({
          type: EToastItemType.Success,
          title: $localize`:@@DialogUserPasswordSetPasswordSetSuccessTitle:Password changed`,
          message: $localize`:@@DialogUserPasswordSetResultSuccessMesssage:You password has been changed successfully`,
        }));
        return true;
      }
      return false;
    }).catch((ex) => {
      this.logger.error(`${ex}`, this.constructor.name);
      this.toastService.toastItemAdd(new ToastItem({
        type: EToastItemType.Danger,
        title: $localize`:@@DialogUserPasswordSetResultFailureTitle:Password change failed`,
        message: $localize`:@@DialogUserPasswordSetResultFailureMesssage:You password could not be changed. Please try it again later. If the problem persists please contact us.`,
        code: JSON.parse(ex.response).errorCode
      }));
      return false;
    });
  }

  public passwordOldInputFieldChanged() {
    this.passwordOldCriteriaValidate();
    this.formValidate();
  }

  public passwordNew1InputFieldChanged() {
    this.passwordNew1CriteriaValidate();
    this.passwordNewMatchingStateValidate();
    this.formValidate();
  }

  public passwordNew2InputFieldChanged() {
    this.passwordNewMatchingStateValidate();
    this.formValidate();
  }

  private formValidate() {
    let formIsValid = true;

    if (this.passwordOldCriteriaValidate() == false) { formIsValid = false; }
    if (this.passwordNew1CriteriaValidate() == false) { formIsValid = false; }
    if (this.passwordNewMatchingStateValidate() == false) { formIsValid = false; }

    this.formIsValid = formIsValid;
    return formIsValid;
  }

  private passwordOldCriteriaValidate() {
    let passwordOldIsValid = true;

    if (this.passwordOld == undefined) { passwordOldIsValid = false; }
    if (this.passwordOld.length <= 0) { passwordOldIsValid = false; }

    this.passwordOldIsValid = passwordOldIsValid;
    return passwordOldIsValid;
  }

  private passwordNew1CriteriaValidate() {
    let passwordNewIsValid = true;

    if (this.passwordNew1 == undefined) { passwordNewIsValid = false; }
    if (this.passwordNew1.length < this.passwordMinLength) { passwordNewIsValid = false; }
    if (this.passwordNew1.length > this.passwordMaxLength) { passwordNewIsValid = false; }

    this.passwordNewIsValid = passwordNewIsValid;
    return passwordNewIsValid;
  }

  private passwordNewMatchingStateValidate() {
    let passwordNewMatching = false;
    if (this.passwordNew1 == this.passwordNew2) { passwordNewMatching = true; }

    if (this.passwordNewMatching != passwordNewMatching) {
      this.passwordNewMatching = passwordNewMatching;
    }

    return passwordNewMatching;
  }

}
